import type { NarrowArgs, NarrowReturn, Scheduler } from './types'

export default (): Scheduler => {
  const timeouts: number[] = []
  const intervals: number[] = []

  document.addEventListener('turbolinks:before-render', clear)

  const promisedTimeout = <F extends (...args: [unknown]) => unknown>(
    callback: F,
    ms: number,
    ...args: NarrowArgs<F>
  ): Promise<NarrowReturn<F>> =>
    new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      const id = setTimeout(async () => {
        try {
          const result = (await callback.apply(this, args)) as NarrowReturn<F>
          resolve(result)
        } catch (e) {
          reject(e as Error)
        }
      }, ms)

      timeouts.push(id)
    })

  function setTimeout<F extends (...args: [unknown]) => unknown>(
    callback: F,
    ms: number,
    ...args: Parameters<F>
  ): number {
    const id = window.setTimeout(callback, ms, ...args)
    timeouts.push(id)
    return id
  }

  function setInterval<F extends (...args: [unknown]) => unknown>(
    callback: F,
    ms: number,
    ...args: Parameters<F>
  ): number {
    const id = window.setInterval(callback, ms, ...args)
    intervals.push(id)
    return id
  }

  function clear(): void {
    timeouts.forEach((id) => clearTimeout(id))
    intervals.forEach((id) => clearInterval(id))

    timeouts.length = 0
    intervals.length = 0
  }

  return {
    promisedTimeout,
    setTimeout,
    setInterval,
    clear,
  }
}
